import { Injectable, computed, effect, signal } from '@angular/core';

export type Theme = 'dark' | 'light';

// 'emg.' prefix per the project rule set: non-secret UI state only, purged by
// prefix on logout. Nothing auth-related ever goes in web storage.
const STORAGE_KEY = 'emg.theme';

/**
 * Reads the theme that the inline bootstrap script in index.html already
 * stamped on <html>. Starting from the DOM rather than from storage means the
 * service can never disagree with what the user is currently looking at.
 */
function currentAppliedTheme(): Theme {
  const applied = document.documentElement.dataset['theme'];
  return applied === 'light' ? 'light' : 'dark';
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly current = signal<Theme>(currentAppliedTheme());

  readonly theme = this.current.asReadonly();
  readonly isLight = computed(() => this.current() === 'light');

  constructor() {
    effect(() => {
      const theme = this.current();
      document.documentElement.setAttribute('data-theme', theme);
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch {
        // Storage is unavailable (private mode, embedded context). The
        // attribute is still applied, the choice just will not survive reload.
      }
    });
  }

  toggle(): void {
    this.current.update((theme) => (theme === 'dark' ? 'light' : 'dark'));
  }

  set(theme: Theme): void {
    this.current.set(theme);
  }
}
